import React from 'react';
import usePushStatus from '../../hooks/usePushStatus';
import { T, RADIUS } from './notificationModel';
import CallAlertsPanel from '../calls/CallAlertsPanel';

const PILL = {
  flex: 1, height: 34, border: 0, borderRadius: RADIUS.pill, background: T.off, fontFamily: 'inherit',
  fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer', transition: 'background-color .2s, color .2s',
};

/**
 * The push panel in the aside (handoff §1 "Push panel"): a status line with a dot, then two
 * pills. Off, it offers TURN ON; on, SEND TEST and TURN OFF. The browser-blocked and
 * iOS-install states explain themselves in the same white panel.
 */
export default function PushPanel({ className = '' }) {
  const { state, busy, error, testResult, subscribe, unsubscribe, sendTest } = usePushStatus();
  const panelStyle = { display: 'flex', flexDirection: 'column', gap: 12, padding: 16, borderRadius: RADIUS.panel, background: T.white };
  if (state === 'loading' || state === 'unsupported') return <div className={className} style={panelStyle}><CallAlertsPanel /></div>;

  const dot = (color) => <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} aria-hidden />;
  const line = (color, text) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{dot(color)}{text}</span>
  );
  const note = (text, color = T.grey500) => <span style={{ fontSize: 10, lineHeight: 1.6, color }}>{text}</span>;

  let body;
  if (state === 'subscribed') {
    body = (
      <>
        {line(T.pos, 'PUSH · ON FOR THIS DEVICE')}
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="pr-push-pill" onClick={sendTest} disabled={busy} style={PILL}>SEND TEST</button>
          <button type="button" className="pr-push-pill" onClick={unsubscribe} disabled={busy} style={{ ...PILL, color: T.grey500 }}>TURN OFF</button>
        </div>
        {testResult && note(testResult.text, testResult.ok ? '#15803D' : T.neg)}
      </>
    );
  } else if (state === 'denied') {
    body = (
      <>
        {line(T.neg, 'PUSH · BLOCKED BY BROWSER')}
        {note("Open this site's settings in your browser and allow notifications, then refresh.")}
      </>
    );
  } else if (state === 'ios-install') {
    body = (
      <>
        {line(T.grey400, 'PUSH · ADD TO HOME SCREEN')}
        {note('iOS only delivers push for installed apps. Tap Share in Safari, then Add to Home Screen, and come back here.')}
      </>
    );
  } else {
    body = (
      <>
        {line(T.grey400, 'PUSH · OFF ON THIS DEVICE')}
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="pr-push-pill" onClick={subscribe} disabled={busy} style={{ ...PILL, background: T.ink, color: '#fff' }}>
            {busy ? 'TURNING ON…' : 'TURN ON'}
          </button>
        </div>
        {error && note(error, T.neg)}
      </>
    );
  }

  return (
    <div className={className} data-testid="push-panel" style={panelStyle}>
      {body}
      <CallAlertsPanel />
    </div>
  );
}
